import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Icon } from '~/components';

import {
  exportCSV,
  getNotificationById,
  getPageData,
} from '../../api/dashboard';
import { getTrackOverwrites } from '../../api/overwrites';
import { getIgnoresBulk } from '../../api/track-ignore';
import {
  bulkLockTracks,
  bulkUnlockTracks,
  getLocksBulk,
} from '../../api/track-lock';
import { MenuItem } from '../../components/DataGrid/components/MenuItem';
import {
  type ColDef,
  ColumnFilterType,
  type DataGridState,
  type ExportCSVPayload,
  type ExportCSVResponse,
  type FilterModel,
} from '../../components/DataGrid/types';
import type {
  DataLoader,
  DataQuery,
} from '../../components/DataGrid/types/query';
import { ViewType } from '../../components/DataGrid/types/views';
import {
  arrayUnique,
  camelToSnake,
  isValidDate,
  serialize,
} from '../../components/DataGrid/utils';
import { Drawer } from '../../components/Drawer';
import type { DrawerActions } from '../../components/Drawer/Drawer';
import { useAmplifyUser } from '../../context/useAmplifyUser';
import type { DashboardRow, DataRowKeyWithPrefix } from '../../types/dashoard';
import { columnDefs } from './columnsDefs';
import { TrackOverwrite } from './components/TrackOverwrite';
import { Details } from './Details';
import { filterColumns } from './filters';
import { searchFilter } from './utils/search';

const LazyAgGridReact = lazy(() =>
  import('../../components/DataGrid').then((module) => ({
    default: module.DataGrid,
  })),
);

const defaultColDef: ColDef = {
  sortable: false,
  resizable: true,
};

const Dashboard = (): JSX.Element => {
  const { t } = useTranslation('label-perf');
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<Partial<DashboardRow>[]>();
  const [bulkMode, setBulkMode] = useState<boolean>(true);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [enrichmentRefresher, setEnrichmentRefresher] = useState<string>('');
  const [overwriteISRCs, setOverwriteISRCs] = useState<string[]>([]);
  const [detailsId, setDetailsId] = useState<string>();
  const [searchParams] = useSearchParams();
  const { user } = useAmplifyUser();

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setDetailsId(id);
    }
  }, [searchParams]);

  const ContextToolbar = useMemo(() => {
    // No Context Toolbar with no selected rows
    if (!selectedRows || selectedRows?.length === 0) {
      return undefined;
    }

    const selectedISRSs = selectedRows
      .filter((row) => row.isrc)
      .map((row) => row.isrc as string);

    const toReviewISRCs = selectedRows
      .filter((row) => !row.trackLock)
      .map((row) => row.isrc as string);

    const lockedISRCs = selectedRows
      .filter((row) => row.trackLock)
      .filter((row) => {
        // if (isAdmin) return true; // Admin can unlock any track
        return row.trackLock!.self; // Only lock owner can unlock
      })
      .map((row) => row.isrc as string);

    if (selectedRows.filter((row) => row.isrc).length === 0) {
      return (
        <div className="flex flex-col items-center gap-sm rounded-sm border px-md py-sm text-center border-emphasis-subtle text-white">
          <div className="mb-xs">
            <Icon
              className="mr-sm text-error-default"
              type="triangle-exclamation"
            />{' '}
            {t('error.ISRCNotSelected')}
          </div>
          <div className="group relative mb-xs w-[5em] cursor-pointer rounded-md border px-sm py-xs text-body-sm hover:bg-secondary-default hover:text-white focus:outline-none">
            <button onClick={() => setBulkMode(false)}>OK</button>
          </div>
        </div>
      );
    }

    const overWriteISRCsHandler = (isrcs: string[]): void => {
      setOverwriteISRCs(isrcs);
    };

    const bulkLockTracksHandler = async (isrcs: string[]): Promise<void> => {
      setBulkLoading(true);
      await bulkLockTracks(isrcs);
      setBulkLoading(false);
      // Remove selection
      setBulkMode(false);
      setTimeout(() => setBulkMode(true), 0);
      // Refresh data
      setEnrichmentRefresher(new Date().toISOString());
    };

    const bulkUnlockTracksHandler = async (isrcs: string[]): Promise<void> => {
      setBulkLoading(true);
      await bulkUnlockTracks(isrcs);
      setBulkLoading(false);
      // Remove selection
      setBulkMode(false);
      setTimeout(() => setBulkMode(true), 0);
      // Refresh data
      setEnrichmentRefresher(new Date().toISOString());
    };

    return (
      <div className="flex items-center">
        {bulkLoading ? (
          <div className="flex items-center align-middle text-white">
            <Icon type="spin" className="mr-sm text-white" />{' '}
            {t('body.processing')}
          </div>
        ) : null}
        {!bulkLoading && (
          <>
            <div className="mr-sm flex items-center gap-xs">
              <MenuItem
                disabled={selectedISRSs.length !== 1}
                icon="marker"
                onClick={() => overWriteISRCsHandler(selectedISRSs)}
                title="Overwrites"
              />
            </div>
            <p className="mr-sm text-white">{t('Bulk actions')}:</p>
            <div className="flex items-center gap-x-sm">
              <MenuItem
                counter={toReviewISRCs.length}
                disabled={toReviewISRCs.length === 0}
                icon="eye"
                onClick={() => bulkLockTracksHandler(toReviewISRCs)}
                title="In Review"
              />
              <MenuItem
                counter={lockedISRCs.length}
                disabled={lockedISRCs.length === 0}
                icon="eye"
                onClick={() => bulkUnlockTracksHandler(lockedISRCs)}
                title="Cancel Review"
              />
            </div>
          </>
        )}
      </div>
    );
  }, [bulkLoading, selectedRows, t]);

  const combineFilters = (
    _search: string | undefined,
    filter: string | undefined,
  ): string => {
    const searchCriteria = _search ? searchFilter(_search) : '';
    const serializedFilter = filter || '';

    if (searchCriteria && serializedFilter) {
      return `${serializedFilter} AND (${searchCriteria})`;
    }

    if (searchCriteria) {
      return searchCriteria;
    }

    return serializedFilter.trim();
  };

  const dataLoader: DataLoader<Partial<DashboardRow>> = useCallback(
    (query: DataQuery<Partial<DashboardRow>>) => {
      const combinedFilter = combineFilters(search, query.filter);
      return getPageData({
        ...query,
        offset: query.nextPageToken
          ? Number(query.nextPageToken.split('=')[1])
          : 0,
        select: query.select,
        order: query.order?.map((s) =>
          s.direction === 'asc' ? s.field : `-${s.field}`,
        ) as DataRowKeyWithPrefix[],
        filter: combinedFilter,
      });
    },
    [search],
  );

  const dataEnricher = useCallback(
    async (data: Partial<DashboardRow>[]): Promise<Partial<DashboardRow>[]> => {
      if (!user) {
        return data;
      }
      try {
        if (data.length > 0 && data[0].isrc) {
          const isrcs = data.map((row) => row.isrc as string);
          const locks = await getLocksBulk(isrcs);
          const ignores = await getIgnoresBulk(isrcs);
          const overwrites = await getTrackOverwrites(isrcs);

          data.forEach((row) => {
            const lock = locks.find((l) => l.isrc === (row.isrc as string));
            row.trackLock = lock && {
              self: lock.created_by.id === user?.id,
              owner: {
                name: lock.created_by.name,
                email: lock.created_by.email,
              },
              createdAt: new Date(lock.created_at),
            };
            const ignore = ignores.find((l) => l.isrc === (row.isrc as string));
            row.trackIgnore = ignore && {
              // TODO: add user id here
              self: ignore.created_by.id === user?.id,
              owner: {
                name: ignore.created_by.name,
                email: ignore.created_by.email,
              },
              createdAt: new Date(ignore.created_at),
            };
            const overwrite = overwrites.find(
              (o) => o.isrc === row.isrc && (o.artist_name || o.track_title),
            );
            row.overwrites = overwrite
              ? {
                  trackTitle: overwrite.track_title,
                  artist: overwrite.artist_name,
                }
              : undefined;
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error enriching data:', (error as Error).message);
      }
      return Promise.resolve(data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrichmentRefresher, user], // enrichmentRefresher is needed to force re-enrichment
  );

  const downloadCSVFile = (url: string): void => {
    try {
      const downloadAnchor = document.createElement('a');
      downloadAnchor.style.display = 'none';
      downloadAnchor.href = url;
      downloadAnchor.download = 'CSV Export';
      downloadAnchor.click();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading CSV file:', error);
    }
  };

  const exportCSVFile = async (context: DataGridState): Promise<void> => {
    const { select, order, filter } = context;
    const serializedFilter = serialize(filter);
    const combinedFilter = combineFilters(search, serializedFilter);

    const gridData: ExportCSVPayload = {
      select: arrayUnique(select.map((s) => camelToSnake(s))),
      order: order.map(
        (o) => `${o.direction === 'desc' ? '-' : ''}${camelToSnake(o.field)}`,
      ),
      filter: combinedFilter,
    };

    setExportLoading(true);
    const res = await exportCSV(gridData).catch((error) => {
      // eslint-disable-next-line no-console
      console.log('error', error);
      setExportLoading(false);
    });
    const url = (res as ExportCSVResponse)?.url;

    if (url) {
      downloadCSVFile(url);
      setExportLoading(false);
    }
  };

  const onExport = (context: DataGridState): void => {
    exportCSVFile(context).catch((error) => {
      // eslint-disable-next-line no-console
      console.log('error', error);
    });
  };

  const urlFiltersLoader = async (): Promise<FilterModel[]> => {
    const q = new URLSearchParams(window.location.search);
    const alertId = q.get('alertId');
    const ts = q.get('ts');
    if (alertId) {
      try {
        const notification = await getNotificationById(Number(alertId)).catch(
          (error) => {
            // eslint-disable-next-line no-console
            console.error('Error loading notification:', error);
            return { model: '[]' };
          },
        );
        const filterModel = JSON.parse(
          notification.model ?? '[]',
        ) as FilterModel[];
        if (ts && isValidDate(ts)) {
          filterModel.push({
            arrayField: false,
            field: 'latestProductTs',
            multiValue: false,
            name: 'Updates since date',
            operand: {
              value: 'greaterOrEqualTo',
              label: 'Greater or equal to',
            },
            type: ColumnFilterType.dateRange,
            value: ts,
          });
        }
        return filterModel;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading filters:', error);
      }
    }
    return [];
  };

  const onDetailsClose = (): void => {
    setDetailsId(undefined);
    navigate('../territories');
  };

  const detailActions: DrawerActions[] = [
    { title: 'Close', handler: onDetailsClose },
  ];
  const enrich = !detailsId && overwriteISRCs.length === 0;

  return (
    <>
      {user ? (
        <div className="flex h-[calc(100%-126px)] flex-wrap">
          <Suspense fallback={<div>Loading Data Grid...</div>}>
            <LazyAgGridReact
              title="territories"
              columnDefs={columnDefs}
              contextToolbarElement={ContextToolbar}
              dataEnricher={enrich ? dataEnricher : undefined}
              dataLoader={dataLoader}
              defaultColDef={defaultColDef}
              enableConfig={true}
              enableExpand={true}
              enableExport={true}
              enableFilters={true}
              exportLoading={exportLoading}
              filterColumns={filterColumns}
              filtersLoader={urlFiltersLoader}
              id="main"
              limit={100}
              onExport={onExport}
              onSearch={setSearch}
              onSelectionChanged={setSelectedRows}
              selectionMode={bulkMode ? 'multi' : undefined}
              viewIcon="microscope"
              views={[
                ViewType.Territories,
                ViewType.Derivative,
                ViewType.TrackRollup,
              ]}
              onViewChanged={(view) => {
                if (view === 'Territories') {
                  return;
                }
                if (view === 'Derivative') {
                  navigate('../derivative');
                }
                if (view === 'TrackRollup') {
                  navigate('../rollup');
                }
              }}
            />
          </Suspense>
          {detailsId ? (
            <Drawer
              actions={detailActions}
              onClose={onDetailsClose}
              open={true}
              size="full"
              title={`ISRC: ${detailsId}`}
            >
              <Details id={detailsId} />
            </Drawer>
          ) : null}
          {overwriteISRCs.length > 0 && (
            <Drawer
              onClose={() => setOverwriteISRCs([])}
              open={true}
              size="md"
              title="Overwrite ISRC"
            >
              <TrackOverwrite
                closeHandler={() => setOverwriteISRCs([])}
                isrc={overwriteISRCs[0]}
              />
            </Drawer>
          )}
        </div>
      ) : null}
    </>
  );
};

Dashboard.displayName = 'Dashboard';

export { Dashboard };
